"use client"

import Back from "@modules/common/icons/back"
import FastDelivery from "@modules/common/icons/fast-delivery"
import Refresh from "@modules/common/icons/refresh"

import Accordion from "./accordion"
import { HttpTypes } from "@medusajs/types"
import T from "@lib/i18n/t"
import { useT } from "@lib/i18n/client"

type ProductTabsProps = {
  product: HttpTypes.StoreProduct
}

const ProductTabs = ({ product }: ProductTabsProps) => {
  const t = useT()
  // Etykiety trafiają do propsa `title` akordeonu, więc muszą być stringami —
  // <T> tu nie zadziała, potrzebny jest tłumacz jako funkcja.
  const tabs = [
    {
      label: t("Informacje o produkcie"),
      component: <ProductInfoTab product={product} />,
    },
    {
      label: t("Dostawa i zwroty"),
      component: <ShippingInfoTab />,
    },
  ]

  return (
    <div className="w-full">
      <Accordion type="multiple">
        {tabs.map((tab, i) => (
          <Accordion.Item
            key={i}
            title={tab.label}
            headingSize="medium"
            value={tab.label}
          >
            {tab.component}
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  )
}

const ProductInfoTab = ({ product }: ProductTabsProps) => {
  const t = useT()
  const meta = (product.metadata ?? {}) as Record<string, unknown>
  const val = (key: string) =>
    typeof meta[key] === "string" && meta[key] ? (meta[key] as string) : null

  // Kolejność od najważniejszego dla kupującego. Wiersze bez danych po prostu
  // nie powstają — starter pokazywał tu kolumnę myślników (m.in. "Wymiary",
  // które dla koszulki i tak nic nie znaczą).
  const rows: { label: string; value: string | null }[] = [
    { label: "Marka", value: val("brand") },
    { label: "Model", value: val("model") },
    { label: "Materiał", value: val("fabric") ?? product.material ?? null },
    { label: "Gramatura", value: val("gsm") },
    { label: "Przędza", value: val("yarn") },
    { label: "Krój", value: val("fit") },
    { label: "Dekolt", value: val("neck") },
    { label: "Rozmiary", value: val("sizes") },
    // Dwa osobne wiersze: koszulka i nadruk powstają w różnych krajach,
    // a jedno pole "kraj pochodzenia" zawsze wprowadzałoby w błąd.
    { label: "Pochodzenie koszulki", value: val("origin") },
    { label: "Miejsce nadruku", value: val("printedIn") },
  ]

  const present = rows.filter((r) => r.value)
  const care = val("care")

  if (!present.length && !care) {
    return null
  }

  return (
    <div className="text-small-regular py-8">
      {/* Jedna kolumna: ta zakładka siedzi w wąskiej (max 300 px) kolumnie
          strony produktu, gdzie dwie kolumny łamią wartości w połowie słowa. */}
      <dl className="flex flex-col gap-y-3">
        {present.map((row) => (
          <div
            key={row.label}
            className="flex justify-between gap-x-4 border-b border-ui-border-base pb-2 last:border-b-0"
          >
            <dt className="shrink-0 font-semibold text-ui-fg-base">
              <T>{row.label}</T>
            </dt>
            <dd className="text-right text-ui-fg-subtle">
              {t(row.value as string)}
            </dd>
          </div>
        ))}
      </dl>

      {care && (
        <div className="mt-6 border-t border-ui-border-base pt-4">
          <span className="font-semibold text-ui-fg-base">
            <T>Pielęgnacja</T>
          </span>
          <p className="mt-1 max-w-md text-ui-fg-subtle">{t(care)}</p>
          <p className="mt-2 max-w-md text-ui-fg-muted">
            <T>Koszulkę z nadrukiem pierz na lewej stronie i nie prasuj po nadruku.</T>
          </p>
        </div>
      )}
    </div>
  )
}

const ShippingInfoTab = () => {
  return (
    <div className="text-small-regular py-8">
      <div className="grid grid-cols-1 gap-y-8">
        <div className="flex items-start gap-x-2">
          <FastDelivery />
          <div>
            <span className="font-semibold"><T>Wysyłka w 1-2 dni</T></span>
            <p className="max-w-sm">
              <T>Paczkę nadajemy w ciągu 1-2 dni roboczych od zaksięgowania wpłaty. Do wyboru kurier albo Paczkomat InPost.</T>
            </p>
          </div>
        </div>
        <div className="flex items-start gap-x-2">
          <Refresh />
          <div>
            <span className="font-semibold"><T>Wymiana rozmiaru</T></span>
            <p className="max-w-sm">
              <T>Rozmiar nie trafiony? Napisz do nas — wymienimy koszulkę na inną, o ile nie była noszona i ma metki.</T>
            </p>
          </div>
        </div>
        <div className="flex items-start gap-x-2">
          <Back />
          <div>
            <span className="font-semibold"><T>14 dni na zwrot</T></span>
            <p className="max-w-sm">
              <T>Masz 14 dni na odstąpienie od umowy bez podawania przyczyny. Odsyłasz produkt, my zwracamy pieniądze.</T>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductTabs
